import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Save, Loader2 } from "lucide-react";
import { ownerApi } from "@/lib/api";
import { Spinner } from "@/components/common/States";

const input = "w-full rounded-xl border border-border bg-background p-2.5 text-sm outline-none focus:border-primary";

const GROUPS = [
  {
    title: "Home screen",
    fields: [
      ["home_hero_title", "Hero title", "area", "Fresh fits, effortlessly."],
      ["home_hero_subtitle", "Hero subtitle", "area", "Dry clean · Wash n Fold · Press · Shoes · Tailoring"],
      ["copy_home_book_button", "Primary button label", "input", "Book a pickup"],
    ],
  },
  {
    title: "Fee explainers (shown on the “i” next to each charge)",
    fields: [
      ["copy_fee_pickup_info", "Pickup & delivery charge", "area", ""],
      ["copy_fee_distance_info", "Distance add-on", "area", ""],
      ["copy_fee_slot_info", "Early/late slot surcharge", "area", ""],
      ["copy_fee_fastservice_info", "Fast-service (Priority/Express/Rapid)", "area", ""],
      ["copy_fee_mintopup_info", "Minimum-wash top-up", "area", ""],
    ],
  },
  {
    title: "Game-points guidance (Review step)",
    fields: [
      ["copy_game_points_title", "Section title", "input", "Use your game points"],
      ["copy_game_points_guidance", "Guidance text", "area", ""],
    ],
  },
  {
    title: "Points earning & validity (Review step)",
    fields: [
      ["copy_earn_preview", "“You'll earn” line (use {points} for the amount)", "area", "You'll earn ~{points} points when this order is completed."],
      ["copy_points_validity_note", "General validity note", "area", ""],
      ["copy_earn_ways_intro", "Ways-to-earn heading", "input", "Three ways to collect points:"],
      ["copy_earn_ways_spend", "Ways to earn — spending (use {rate})", "area", "Spending — every order earns points (₹{rate} spent = 1 point)."],
      ["copy_earn_ways_games", "Ways to earn — games", "area", "Games — play in the Games hub to win bonus points."],
      ["copy_earn_ways_share", "Ways to earn — sharing/referral", "area", "Sharing — share your referral code; when a friend's first order completes, you both earn bonus points."],
    ],
  },
  {
    title: "Guest checkout",
    fields: [["copy_guest_nudge", "Sign-in nudge (shown to guests at Review)", "area", ""]],
  },
];

export default function OwnerContent() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["ownerSettings"], queryFn: ownerApi.getSettings });
  const [vals, setVals] = useState(null);

  useEffect(() => {
    if (query.data) setVals(query.data);
  }, [query.data]);

  const set = (k, v) => setVals((s) => ({ ...s, [k]: v }));

  const save = useMutation({
    mutationFn: () => {
      const values = {};
      for (const g of GROUPS) for (const [k] of g.fields) values[k] = vals[k] ?? "";
      return ownerApi.updateSettings(values);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ownerSettings"] });
      queryClient.invalidateQueries({ queryKey: ["shop"] });
      toast.success("Content saved — live on the web app");
    },
    onError: (e) => toast.error(e.message || "Could not save content"),
  });

  if (query.isPending || !vals) return <Spinner />;

  return (
    <div className="space-y-4 pb-24">
      <p className="text-sm text-muted-foreground">Edit the wording customers see on the web app. Leave a field blank to use the built-in default text.</p>
      {GROUPS.map((g) => (
        <section key={g.title} className="rounded-2xl border border-border bg-card p-5">
          <h3 className="mb-3 font-display text-sm font-bold">{g.title}</h3>
          <div className="space-y-3">
            {g.fields.map(([k, label, kind, placeholder]) => (
              <label key={k} className="block">
                <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
                {kind === "area" ? (
                  <textarea rows={2} className={input} value={vals[k] ?? ""} onChange={(e) => set(k, e.target.value)} placeholder={placeholder} data-testid={`content-${k}`} />
                ) : (
                  <input className={input} value={vals[k] ?? ""} onChange={(e) => set(k, e.target.value)} placeholder={placeholder} data-testid={`content-${k}`} />
                )}
              </label>
            ))}
          </div>
        </section>
      ))}
      <div className="sticky bottom-4 z-10">
        <button onClick={() => save.mutate()} disabled={save.isPending} data-testid="owner-content-save" className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground shadow-lg disabled:opacity-60">
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save content
        </button>
      </div>
    </div>
  );
}
